import React from "react";

import { formatNumber, formatNumericCurrency, getCurrencySymbol, useTranslation } from "utils";

import { CurrencyCode } from "consts";

import { AlertBanner, Flex, Icon, Typography } from "components";

type PayoutContextStructureCardProps = {
  currency?: CurrencyCode;
  eligiblePayout: number;
  pricePerT: number;
  maxEligibleImpact: number;
  totalPayoutMax: number;
  onLearnMore: () => void;
};

const formatTonnes = (value: number) => `${formatNumber({ number: value })}t`;

export const PayoutContextStructureCard = ({
  currency,
  eligiblePayout,
  pricePerT,
  maxEligibleImpact,
  totalPayoutMax,
  onLearnMore,
}: PayoutContextStructureCardProps) => {
  const { t } = useTranslation("fieldTimeline");

  return (
    <Flex vertical className="pcd-card pcd-structure-card">
      <Typography variant="paragraph-l" bold>
        {t("impactPanel.payoutImpact.contextDrawer.structure.title")}
      </Typography>
      <Typography variant="paragraph-m" className="pcd-structure-description">
        {t("impactPanel.payoutImpact.contextDrawer.structure.description")}
      </Typography>
      <div className="pcd-structure-bar">
        <Flex align="center" justify="space-between" className="pcd-structure-amounts">
          <Typography variant="h1" className="amount">
            {formatNumericCurrency(eligiblePayout, { decimals: 0, currency })}
          </Typography>
          {totalPayoutMax > eligiblePayout ? (
            <Typography variant="h1" className="amount">
              {formatNumericCurrency(totalPayoutMax - eligiblePayout, { decimals: 0, currency, signDisplay: "never" })}
            </Typography>
          ) : (
            <Flex align="baseline" className="amount" gap=".375rem">
              <Typography variant="h1">{getCurrencySymbol(currency)}-</Typography>
              <Typography variant="paragraph-s">{t("impactPanel.payoutImpact.contextDrawer.common.unknown")}</Typography>
            </Flex>
          )}
        </Flex>
        <div className="eligible" />
        <div className="non-eligible" />
      </div>
      <Flex align="center" className="pcd-structure-legend">
        <Flex justify="flex-start" gap="0.25rem">
          <span className="pcd-legend-badge">1</span>
          <Typography variant="paragraph-s">{t("impactPanel.payoutImpact.contextDrawer.structure.eligibleLabel")}</Typography>
        </Flex>
        <Flex justify="flex-start" gap="0.25rem">
          <span className="pcd-legend-badge">2</span>
          <Typography variant="paragraph-s">{t("impactPanel.payoutImpact.contextDrawer.structure.nonEligibleLabel")}</Typography>
        </Flex>
      </Flex>
      <Flex vertical className="pcd-structure-details" gap="1.5rem">
        <Flex className="pcd-structure-detail-row" gap="0.5rem">
          <span className="pcd-structure-dot" />
          <Flex vertical className="pcd-structure-detail-copy" gap="0.25rem">
            <Typography variant="paragraph-s" bold className="pcd-structure-detail-title">
              {t("impactPanel.payoutImpact.contextDrawer.structure.eligibleLabel")}
            </Typography>
            <Typography variant="paragraph-s" className="pcd-structure-detail-text">
              {t("impactPanel.payoutImpact.contextDrawer.structure.eligibleDescription")}
            </Typography>
          </Flex>
        </Flex>
        <Flex className="pcd-structure-detail-row" gap="0.5rem">
          <span className="pcd-structure-striped-dot" />
          <Flex vertical className="pcd-structure-detail-copy" gap="0.25rem">
            <Typography variant="paragraph-s" bold className="pcd-structure-detail-title">
              {t("impactPanel.payoutImpact.contextDrawer.structure.nonEligibleLabel")}
            </Typography>
            <Typography variant="paragraph-s" className="pcd-structure-detail-text">
              {t("impactPanel.payoutImpact.contextDrawer.structure.nonEligibleDescription")}
            </Typography>
          </Flex>
        </Flex>
      </Flex>
      <AlertBanner
        className="pcd-structure-banner"
        iconName="tabler/bulb"
        iconSize={20}
        iconAlign="start"
        headingSize="paragraph-s"
        descriptionSize="paragraph-s"
        isTitleBold
        title={t("impactPanel.payoutImpact.contextDrawer.increaseEligible.title")}
        description={t("impactPanel.payoutImpact.contextDrawer.increaseEligible.description")}
        link
        linkText={t("impactPanel.payoutImpact.learnMore")}
        onClick={onLearnMore}
      />
      <Flex vertical className="pcd-breakdown pcd-breakdown-edge pcd-breakdown-structure" gap="0.5rem">
        <Flex align="center" className="pcd-breakdown-header" gap="0.25rem">
          <Icon iconName="tabler/chevron-down" size="m-light" />
          <Typography variant="paragraph-m" bold>
            {t("impactPanel.payoutImpact.contextDrawer.structure.eligibleBreakdown")}
          </Typography>
        </Flex>
        <Flex vertical className="pcd-breakdown-body pcd-breakdown-body-structure" gap="0.5rem">
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.structure.pricePerT")}</Typography>
            <Typography variant="paragraph-m">{formatNumericCurrency(pricePerT, { decimals: 0, currency })}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.structure.maxImpact")}</Typography>
            <Typography variant="paragraph-m">{`+${formatTonnes(maxEligibleImpact)}`}</Typography>
          </Flex>
          <Flex justify="space-between" className="row">
            <Typography variant="paragraph-m">{t("impactPanel.payoutImpact.contextDrawer.structure.calculation")}</Typography>
            <Typography variant="paragraph-m">{`${formatTonnes(maxEligibleImpact)} x ${formatNumericCurrency(pricePerT, {
              decimals: 0,
              currency,
            })} / t = ${formatNumericCurrency(eligiblePayout, { decimals: 0, currency })}`}</Typography>
          </Flex>
        </Flex>
        <Flex justify="flex-end" className="pcd-breakdown-total margin-top-100">
          <Typography variant="h1">{formatNumericCurrency(eligiblePayout, { decimals: 0, currency })}</Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
